import {
  FormControl,
  Grid,
  Drawer,
  Typography,
  LinearProgress,
  InputLabel,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DemoContainer, DemoItem } from '@mui/x-date-pickers/internals/demo';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { NumericFormat } from 'react-number-format';
import { IComandaEtapaReunioes, IEtapa } from '../../../../../commons/types/crm';
import { IAuth, ModeloComercial } from '../../../../../commons/types/usuatios';
import { ButtonStato } from '../../../../../commons/styleds/buttons';
import { IconDrawer } from '../../../../../commons/styleds/icons';
import { SelectStato } from '../../../../../commons/styleds/selects';
import { TextFieldStato } from '../../../../../commons/styleds/textFields';
import { GridItemCadastroBtnSalvar } from '../../../../../commons/styleds/grids';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useRecoilValue } from 'recoil';
import { authAtom } from '../../../../../states/usuarios/AuthState';
import { useTranslation } from '../../../../../commons/useTranslasion';
import { ptBR, esES } from '@mui/material/locale';
import { useDateFormatters } from '../../../../../utils/useDateFormatters';

dayjs.extend(utc);
dayjs.extend(timezone);

interface IDrawerReunioes {
  open: boolean;
  setOpen: Function;
  editando: boolean;
  setEditando: Function;
  reuniao: IComandaEtapaReunioes;
  setReuniao: Function;
  etapasSelect: IEtapa[];
  idComanda: number;
  loadingSave: boolean;
  consultores: IAuth[];
  adicionarNaLista: any;
  revisarReuniao: Function;
  buComanda?: number;
  modeloComercial?: ModeloComercial;
}

const DrawerReunioes: React.FC<IDrawerReunioes> = ({
  open,
  setOpen,
  editando,
  setEditando,
  reuniao,
  setReuniao,
  etapasSelect,
  idComanda,
  loadingSave,
  consultores,
  adicionarNaLista,
  revisarReuniao,
  buComanda,
  modeloComercial,
}) => {
  const { translate, language } = useTranslation();
  const { formatoDataDayjs, localeDayjs } = useDateFormatters();
  const lang = language ?? "pt-br";
  let locale = ptBR;
  switch (lang.slice(0, 2)) {
    case "es":
      locale = esES
      break;
  }
  
  const authState = useRecoilValue(authAtom);

  const closeDrawer = () => {
    setEditando(false);
    setOpen(false);
  };

  const handleChangeConsultor = (event: any) => {
    const consultorOnboarding = consultores.find((f) => f.id === event.target.value);
    if (!consultorOnboarding) return;

    let valor: number | undefined = 0;
    const tipo = consultorOnboarding.consultorTipos?.find((t) => t.idBU == buComanda);
    if (tipo)
      valor = modeloComercial == ModeloComercial.B2B ? tipo.valorHoraB2B : tipo.valorHoraB2C;

    setReuniao({
      ...reuniao,
      idConsultorResponsavel: event.target.value,
      valorHora: valor,
    });
  };

  return (
    <Drawer
      style={{ minWidth: '250px' }}
      anchor={'right'}
      open={open}
      onClose={() => closeDrawer()}
    >
      <Grid item xs={12} md={12} style={{ padding: '90px 30px', maxWidth: '500px', width: 'auto' }}>
        <Grid item xs={12} md={12} style={{ display: 'flex', justifyContent: 'center' }}>
          <Grid item xs={2} md={2}>
            <IconDrawer onClick={() => closeDrawer()}>close</IconDrawer>
          </Grid>
          <Grid item xs={10} md={10}>
            <Typography variant="h5" sx={{ paddingBottom: '20px' }}>
              {editando ? translate('acoesReuniao') : translate('agendarReuniao')}
            </Typography>
          </Grid>
        </Grid>

        <FormControl fullWidth>
          <InputLabel id="demo-simple-select-label">{translate('etapas')}</InputLabel>
          <SelectStato
            required
            label={translate('etapas')}
            disabled={editando}
            value={reuniao.tbEtapaId}
            onChange={(event: any) => {
              setReuniao({
                ...reuniao,
                tbEtapaId: event.target.value,
                comandaId: idComanda!,
              });
            }}
          >
            {etapasSelect?.map((etapa: IEtapa, index: number) => {
              return (
                <MenuItem value={etapa.id} key={index}>
                  {etapa.nome}
                </MenuItem>
              );
            })}
          </SelectStato>
        </FormControl>

        <FormControl required fullWidth variant="outlined" sx={{ marginTop: '20px' }}>
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale={localeDayjs}
            localeText={{
              clearButtonLabel: 'Empty',
              todayButtonLabel: 'Now',
            }}
          >
            <DesktopDatePicker
              value={reuniao.dtPrevista ? dayjs(reuniao.dtPrevista).startOf('day') : null}
              label={translate('dataPrevista')}
              format={formatoDataDayjs}
              defaultValue={dayjs(reuniao.dtPrevista).startOf('day')}
              onChange={(value: any) => {
                const updatedDate = dayjs(value).startOf('day').toDate();
                setReuniao({ ...reuniao, dtPrevista: updatedDate });
              }}
            />
          </LocalizationProvider>
        </FormControl>

        <FormControl required fullWidth variant="outlined" sx={{ marginTop: '20px' }}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={localeDayjs}>
            <DemoContainer
              components={['MobileTimePicker', 'MobileTimePicker', 'MobileTimePicker']}
            >
              <DemoItem label={translate('horaInicio')}>
                <TimePicker
                  sx={{ width: '100% !important' }}
                  views={['hours', 'minutes']}
                  format="HH:mm"
                  label={translate('horaInicio')}
                  ampm={false}
                  ampmInClock={false}
                  value={reuniao.dtRealizadaInicio ? dayjs(reuniao.dtRealizadaInicio) : null}
                  onChange={(newValue) => {
                    const localDate = newValue
                      ? dayjs(newValue).format('YYYY-MM-DDTHH:mm:ss')
                      : null;
                    setReuniao({
                      ...reuniao,
                      dtRealizadaInicio: localDate,
                    });
                  }}
                  slotProps={{
                    textField: {
                      required: true,
                      fullWidth: true,
                      variant: 'outlined',
                    },
                  }}
                />
              </DemoItem>
            </DemoContainer>
          </LocalizationProvider>
        </FormControl>

        <FormControl required fullWidth variant="outlined" sx={{ marginTop: '20px' }}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={localeDayjs}>
            <DemoContainer
              components={['MobileTimePicker', 'MobileTimePicker', 'MobileTimePicker']}
            >
              <DemoItem label={translate('horaFim')}>
                <TimePicker
                  sx={{ width: '100% !important' }}
                  views={['hours', 'minutes']}
                  format="HH:mm"
                  label={translate('horaFim')}
                  ampm={false}
                  ampmInClock={false}
                  value={reuniao.dtRealizadaFim ? dayjs(reuniao.dtRealizadaFim) : null}
                  onChange={(newValue) => {
                    const localDate = newValue ? dayjs(newValue) : null;
                    setReuniao({
                      ...reuniao,
                      dtRealizadaFim: localDate ? localDate.format() : null,
                    });
                  }}
                  slotProps={{
                    textField: {
                      required: true,
                      fullWidth: true,
                      variant: 'outlined',
                    },
                  }}
                />
              </DemoItem>
            </DemoContainer>
          </LocalizationProvider>
        </FormControl>

        <FormControl required fullWidth variant="outlined" sx={{ marginTop: '20px' }}>
          <InputLabel id="demo-simple-select-label">{translate('consultores')}</InputLabel>
          <SelectStato
            required
            label={translate('consultores')}
            value={reuniao.idConsultorResponsavel}
            onChange={(event: any) => {
              handleChangeConsultor(event);
            }}
          >
            {consultores?.map((consultor: IAuth, index: number) => {
              return (
                <MenuItem value={consultor.id} key={index}>
                  {consultor.nome}
                </MenuItem>
              );
            })}
          </SelectStato>
        </FormControl>

        {loadingSave && <LinearProgress />}

        {reuniao.status != 'APROVADA' ? (
          <GridItemCadastroBtnSalvar item xs={11} md={11}>
            <ButtonStato variant="contained" onClick={adicionarNaLista}>
              {translate('agendarReuniao')}
            </ButtonStato>
            {editando && (
              <>
                <Tooltip
                  title={
                    reuniao.idConsultorResponsavel !== authState?.id
                      ? translate('voceNaoEConsultorResponsavel')
                      : ''
                  }
                >
                  <span>
                    <ButtonStato
                      variant="contained"
                      onClick={() => revisarReuniao('concluir')}
                      disabled={reuniao.idConsultorResponsavel !== authState?.id}
                      color="success"
                    >
                      {translate('concluir')}
                    </ButtonStato>
                  </span>
                </Tooltip>

                <Tooltip
                  title={
                    reuniao.idConsultorResponsavel !== authState?.id
                      ? translate('voceNaoEConsultorResponsavel')
                      : ''
                  }
                >
                  <span>
                    <ButtonStato
                      variant="contained"
                      disabled={reuniao.idConsultorResponsavel !== authState?.id}
                      onClick={() => revisarReuniao('noShow')}
                      color="error"
                    >
                      {translate('noShow')}
                    </ButtonStato>
                  </span>
                </Tooltip>
              </>
            )}
          </GridItemCadastroBtnSalvar>
        ) : (
          <Typography>{translate('reuniaoAprovada')}</Typography>
        )}
      </Grid>
    </Drawer>
  );
};

export default DrawerReunioes;
